import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Button,
  SimpleGrid,
  Flex,
  Text,
} from '@chakra-ui/react'
import { useTranslation } from 'next-i18next/pages'
import { useEffect, useState } from 'react'
import { useQuery as useApolloQuery } from '@apollo/client'
import { USER_LOOTBOX_ORDERS } from '@/api/query'
import { LootboxMarketOrderType, LootboxMarketOrdersOrderBy } from '@/__generated__/graphql'
import { NoResultsFallback } from '@/components/molecules/NoResultsFallback/NoResultsFallback'
import { useLootboxContext } from '@/context/LootboxProvider'
import { LootboxLimited, LootboxMetadata } from '../../templates/transformLootboxRewards'
import { useSession } from '@/context/SessionProvider'
import { MsgExecuteContractCompat } from '@ninjablaze/sdk-ts'
import { LootboxMarketContractExecuteMsgBuilder } from '@/api/lootbox/LootboxMarketContract.msg-builder'
import { LOOTBOX_MARKET_ADDRESS } from '@/constants/constants'
import { useMutation } from '@tanstack/react-query'
import { useApolloClient } from '@apollo/client'

export const LootboxUsersOrdersModal = ({ onClose, address }: { onClose: () => void; address: string }) => {
  const { t } = useTranslation()
  return (
    <Modal size={'lg'} isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent marginLeft={2.5} marginRight={2.5}>
        <ModalCloseButton />
        <ModalHeader pb={2} fontSize="xl" fontWeight="700">
          {t('lootbox.your-orders')}
        </ModalHeader>
        <ModalBody pb={5}>
          <UserOrders address={address} />
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const UserOrders = ({ address }: { address: string }) => {
  const { t } = useTranslation()
  const [listType, setListType] = useState<LootboxMarketOrderType>(LootboxMarketOrderType.Buy)

  const { data, refetch } = useApolloQuery(USER_LOOTBOX_ORDERS, {
    variables: {
      address,
      orderBy: [
        LootboxMarketOrdersOrderBy.LootboxAsc,
        listType == LootboxMarketOrderType.Buy
          ? LootboxMarketOrdersOrderBy.PriceAsc
          : LootboxMarketOrdersOrderBy.PriceDesc,
      ],
    },
    pollInterval: 10_000,
  })

  useEffect(() => {
    refetch()
  }, [])

  const orders = data?.lootboxMarketOrders?.nodes
    .filter((order) => order?.type === listType)
    .map((order) => ({
      id: order?.orderId,
      price: order?.price,
      lootbox: order?.lootbox,
    }))

  return (
    <>
      <SimpleGrid mb={3} p={1} gap={1} rounded={'2xl'} bg={'ninja.500'} columns={2} maxW={'500px'}>
        <Button
          onClick={() => setListType(LootboxMarketOrderType.Buy)}
          h={10}
          color={listType === LootboxMarketOrderType.Buy ? 'white' : 'ninja.300'}
          bg={listType === LootboxMarketOrderType.Buy ? 'ninja.800' : 'transparent'}
          _hover={{
            bg: 'ninja.900',
            color: 'white',
          }}
          rounded={'2xl'}
          variant={'unstyled'}
          display={'flex'}
          gap={2}
        >
          {t('lootbox.buy')}
        </Button>
        <Button
          onClick={() => setListType(LootboxMarketOrderType.Sell)}
          h={10}
          color={listType === LootboxMarketOrderType.Sell ? 'white' : 'ninja.300'}
          bg={listType === LootboxMarketOrderType.Sell ? 'ninja.800' : 'transparent'}
          _hover={{
            bg: 'ninja.900',
            color: 'white',
          }}
          rounded={'2xl'}
          variant={'unstyled'}
          display={'flex'}
          gap={2}
        >
          {t('lootbox.sell')}
        </Button>
      </SimpleGrid>
      {!orders?.length ? (
        <NoResultsFallback styles={{ mb: 0 }} text={t('messages.no-active-orders')} />
      ) : (
        <Box
          maxH={400}
          overflowY={'auto'}
          pr={1}
          css={{
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-track': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#15143d',
              borderRadius: '24px',
            },
          }}
        >
          {orders?.map((order) => <OrderItem key={order.id} order={order} />)}
        </Box>
      )}
    </>
  )
}

const OrderItem = ({
  order,
}: {
  order: {
    id: string
    price: string
    lootbox?: string
  }
}) => {
  const { t } = useTranslation()
  const { getLootboxCollectionByDenom } = useLootboxContext()
  const { sendTransaction, account } = useSession()
  const [isLoading, setLoading] = useState(false)
  const queryApolloClient = useApolloClient()

  if (!order.lootbox) {
    return null
  }

  const lootbox = getLootboxCollectionByDenom(order.lootbox) as LootboxLimited & LootboxMetadata

  const { mutate } = useMutation({
    onError: () => {
      setLoading(false)
    },
    mutationFn: async (id: any) => {
      setLoading(true)
      const tx_result = await sendTransaction(
        MsgExecuteContractCompat.fromJSON({
          sender: account!.injectiveAddress,
          contractAddress: LOOTBOX_MARKET_ADDRESS,
          funds: [],
          msg: LootboxMarketContractExecuteMsgBuilder.removeOrder({
            orderId: id,
          }),
        })
      )

      tx_result?.height
      if (tx_result?.code == 0) {
        return id
      }

      throw Error()
    },
    onSuccess: () => {
      setTimeout(() => {
        queryApolloClient.refetchQueries({
          include: ['GetUserNumberLootboxOrders'],
        })
      }, 200)
    },
  })

  return (
    <Flex mb={3} _last={{ mb: 0 }} gap={2} bg={'ninja.500'} rounded={'xl'}>
      <Flex
        flexShrink={0}
        w={20}
        h={20}
        justifyContent={'center'}
        alignItems={'center'}
        boxShadow={`0px 0px 16px 0px ${lootbox.image.shadowColor} inset`}
        rounded={'xl'}
      >
        {/* <ChakraImage
          width={40}
          height={40}
          w={'42px'}
          h={'42px'}
          src={getLootboxImagePrefix(lootbox.contract) + lootbox.image.src}
          alt=""
        /> */}
      </Flex>
      <Flex flexBasis={'full'} p={1.5} justifyContent={'space-between'} pos={'relative'}>
        <Box>
          <Text fontWeight={700} mb={0.5}>
            {lootbox.title}
          </Text>
          <Flex gap={6}>
            <Box>
              <Text mb={0.5} color={'ninja.200'} fontWeight={700} fontSize={'xs'}>
                {t('common.price')}
              </Text>
              {/* <Amount value={formatAmount(order.price)} /> */}
            </Box>
            <Box>
              <Text mb={0.5} color={'ninja.200'} fontWeight={700} fontSize={'xs'}>
                {t('common.count')}
              </Text>
              <Text fontSize={'sm'} fontWeight={700}>
                1
              </Text>
            </Box>
          </Flex>
        </Box>
        <Button
          pos={{ base: 'absolute', sm: 'static' }}
          right={1.5}
          top={1.5}
          h={{ base: '24px', sm: 'full' }}
          rounded={{ base: 'lg', sm: 'xl' }}
          bg={'ninja.400'}
          variant={'secondary'}
          px={{ base: 2, sm: 6 }}
          fontSize={{ base: 'xs', sm: 'sm' }}
          isLoading={isLoading}
          onClick={() => mutate(order.id)}
        >
          {t('common.cancel')}
        </Button>
      </Flex>
    </Flex>
  )
}
